import React, { FC, ChangeEvent, KeyboardEvent, useEffect, useState, useRef } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { withRouter, RouteComponentProps } from 'react-router';
import { OperationVariables } from 'react-apollo';
import { ApolloQueryResult } from 'apollo-client';
import { ClickAwayListener, Grid, Input, RootRef } from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import { compose } from 'recompose';
import classNames from 'classnames';
import getScrollbarSize from 'dom-helpers/scrollbarSize';

import { SuggestionTypes } from 'types/app';
import { GetUserSearchesDocument, SearchSuggestDocument } from 'api';
import { withApolloClient, IWithApolloClient } from 'providers';
import { search as searchPath } from 'paths';
import { useScrollDisable } from 'hooks';

import { EndAdornment, List, Recent, StartAdornment, Suggestion } from '../components/Search';

interface IProps {
  sidebar?: boolean;
}

interface IPropsEnhanced
  extends IProps,
    IWithApolloClient,
    WithTranslation,
    RouteComponentProps,
    WithStyles<typeof styles> {}

const STORE_SEARCH_LENGTH = 4;
const MAX_RECENTS = 4;

const styles = (theme: Theme) =>
  createStyles({
    root: {
      paddingLeft: 0,
      backgroundColor: 'transparent',
      borderRadius: 0,
      fontWeight: theme.typography.fontWeightBold,
    },
    sidebar: {
      fontSize: 16,
      '& > div': {
        marginRight: theme.spacing(2),
      },
    },
    input: {
      paddingLeft: 0,
    },
    gridItem: {
      position: 'relative',
    },
  });

const enhancers = compose<IPropsEnhanced, IProps>(
  withApolloClient,
  withRouter,
  withTranslation(),
  withStyles(styles),
);

const SCROLL_BLOCK_KEY = 'search';

// TODO: Refactor to use general search component
const Search: FC<IPropsEnhanced> = ({ classes, apolloClient, history, sidebar, t }) => {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [recents, setRecents] = useState([]);

  const inputEl = useRef(null);
  const recentsListEl = useRef(null);
  const suggestedListEl = useRef(null);

  const showRecent = focused && !query && !!recents.length;
  const showSuggestions = focused && !!query && !!suggestions.length;

  const rootClasses = classNames(classes.root, {
    [classes.sidebar]: sidebar,
  });

  useEffect(() => {
    fetchUserSearches();
  }, []);

  useEffect(() => {
    showRecent || showSuggestions ? setStyle() : resetStyle();
  });

  function getListEl() {
    return showRecent ? recentsListEl : suggestedListEl;
  }

  function resetStyle() {
    useScrollDisable(SCROLL_BLOCK_KEY, false, true);
    document.body.style.paddingRight = null;
  }

  function setStyle() {
    useScrollDisable(SCROLL_BLOCK_KEY, true, true);
    document.body.style.paddingRight = `${getScrollbarSize()}px`;
  }

  async function fetchUserSearches() {
    const { data } = await apolloClient.query({
      query: GetUserSearchesDocument,
      variables: { limit: MAX_RECENTS },
    });

    setRecents(data.getUserSearches);
  }

  function updateUserSearches(text: string) {
    if (text.length < STORE_SEARCH_LENGTH) {
      return;
    }

    if (recents.length === MAX_RECENTS) {
      recents.pop();
    }

    recents.unshift(text);
    setRecents(recents);
  }

  async function handleChange(event: ChangeEvent<HTMLInputElement>) {
    setQuery(event.target.value);

    if (event.target.value.length < 1) {
      return setSuggestions([]);
    }

    const result: ApolloQueryResult<OperationVariables> = await apolloClient.query({
      query: SearchSuggestDocument,
      variables: { prefix: event.target.value },
    });

    if (result.data && result.data.suggest) {
      setSuggestions(result.data.suggest.results);
    }
  }

  function handleListKeyDown(event: KeyboardEvent) {
    const { key } = event;

    if (!showRecent && !showSuggestions) {
      return;
    }

    const listEl = getListEl().current;
    const isFirst = document.activeElement === listEl.querySelector('li:first-child');
    const isLast = document.activeElement === listEl.querySelector('li:last-child');

    if (key === 'ArrowUp') {
      if (isFirst) {
        inputEl.current.focus();
      } else {
        const { previousElementSibling } = document.activeElement;
        (previousElementSibling as HTMLLIElement).focus();
      }
    }

    if (key === 'ArrowDown') {
      if (isLast) {
        inputEl.current.focus();
      } else {
        const { nextElementSibling } = document.activeElement;
        (nextElementSibling as HTMLLIElement).focus();
      }
    }
  }

  function handleInputKeyDown(event: KeyboardEvent) {
    const { key } = event;

    if (key === 'Enter' && !!query) {
      return handleSearch(query);
    }

    if (!showRecent && !showSuggestions) {
      return;
    }

    const listEl = getListEl().current;

    if (key === 'ArrowDown') {
      listEl.querySelector('li:first-child').focus();
    } else if (key === 'ArrowUp') {
      listEl.querySelector('li:last-child').focus();
    }
  }

  function handleClear() {
    setQuery('');
    setSuggestions([]);
    inputEl.current.focus();
  }

  function handleSearch(text: string) {
    setQuery('');
    setSuggestions([]);
    setFocused(false);
    inputEl.current.blur();
    updateUserSearches(text);

    history.push(`${searchPath}?q=${encodeURIComponent(text)}`);
  }

  function handleVisit(type: SuggestionTypes, id: string) {
    if (!query) {
      return;
    }

    setQuery('');
    setSuggestions([]);
    setFocused(false);

    history.push(`/${type}/${id}`);
  }

  function renderRecents() {
    return (
      <RootRef rootRef={recentsListEl}>
        <List i18nKey="common:recent" onKeyDown={handleListKeyDown}>
          {recents.map(text => (
            <Recent key={text} text={text} onClick={handleSearch} />
          ))}
        </List>
      </RootRef>
    );
  }

  function renderSuggested() {
    return (
      <RootRef rootRef={suggestedListEl}>
        <List i18nKey="common:suggested" onKeyDown={handleListKeyDown}>
          {suggestions.map(suggestion => (
            <Suggestion key={suggestion.id} suggestion={suggestion} onClick={handleVisit} />
          ))}
        </List>
      </RootRef>
    );
  }

  return (
    <ClickAwayListener onClickAway={() => setFocused(false)}>
      <Grid container direction="column">
        <Grid item>
          <Input
            fullWidth
            value={query}
            onFocus={() => setFocused(true)}
            onChange={handleChange}
            onKeyDown={handleInputKeyDown}
            classes={{ root: rootClasses, input: classes.input }}
            placeholder={t('common:search')}
            inputRef={inputEl}
            startAdornment={<StartAdornment focused={focused || !!query} />}
            endAdornment={!!query && <EndAdornment query={query} onClear={handleClear} />}
          />
        </Grid>
        <Grid item className={classes.gridItem}>
          {showRecent && renderRecents()}
          {showSuggestions && renderSuggested()}
        </Grid>
      </Grid>
    </ClickAwayListener>
  );
};

export default enhancers(Search);
